import { useEffect, useRef, useState } from "react";
import Row from "antd/es/row";
import Col from "antd/es/col";
import Drawer from "antd/es/drawer";
import { withTranslation, TFunction } from "react-i18next";
import { Link } from "react-router-dom";
import Container from "../../common/Container";
import LoginModal from "../LoginModal";
import ProfileMenu from "./ProfileMenu";
import { useAuth } from "../../contexts/AuthContext";
import {
  HeaderSection,
  LogoContainer,
  Burger,
  MobileCluster,
  NotHidden,
  Menu,
  CustomNavLinkSmall,
  Label,
  Outline,
  LogoTitle,
  LoginButton,
  ProfileWrapper,
  ProfileMenuPanel,
  ProfileMenuItem,
} from "./styles";

// Three horizontal lines icon (like SF Symbol "line.3.horizontal")
const SettingsIcon = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 20 20"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.8"
    strokeLinecap="round"
    aria-hidden="true"
  >
    <line x1="3" y1="5" x2="17" y2="5" />
    <line x1="3" y1="10" x2="17" y2="10" />
    <line x1="3" y1="15" x2="17" y2="15" />
  </svg>
);

const SettingsMenu = ({
  t,
  i18n,
}: {
  t: TFunction;
  i18n: { language: string; changeLanguage: (lng: string) => void };
}) => {
  const auth = useAuth();
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);

  // Close on outside click.
  useEffect(() => {
    if (!open) return;
    const onPointerDown = (e: PointerEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("pointerdown", onPointerDown);
    return () => document.removeEventListener("pointerdown", onPointerDown);
  }, [open]);

  // Close on Esc.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open]);

  const handleSwitchLanguage = () => {
    i18n.changeLanguage(i18n.language === "en" ? "cn" : "en");
    setOpen(false);
  };

  const handleLogout = () => {
    setOpen(false);
    auth.logout();
  };

  return (
    <ProfileWrapper ref={wrapperRef}>
      <button
        type="button"
        onClick={() => setOpen((prev) => !prev)}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={t("settingsMenu")}
        style={{
          background: "transparent",
          border: "none",
          cursor: "pointer",
          padding: 6,
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          color: "#1d1d1f",
          borderRadius: 8,
        }}
      >
        <SettingsIcon />
      </button>
      {open && (
        <ProfileMenuPanel role="menu">
          <ProfileMenuItem
            type="button"
            role="menuitem"
            onClick={handleSwitchLanguage}
          >
            {i18n.language === "en" ? "中文" : "English"}
          </ProfileMenuItem>
          {auth.isAuthenticated && (
            <ProfileMenuItem
              type="button"
              role="menuitem"
              onClick={handleLogout}
            >
              {t("profileLogOut")}
            </ProfileMenuItem>
          )}
        </ProfileMenuPanel>
      )}
    </ProfileWrapper>
  );
};

const Header = ({ t, i18n }: { t: TFunction; i18n: any }) => {
  const [visible, setVisibility] = useState(false);
  const [loginOpen, setLoginOpen] = useState(false);
  const auth = useAuth();

  const toggleButton = () => {
    setVisibility(!visible);
  };

  const openLogin = () => {
    setVisibility(false);
    setLoginOpen(true);
  };

  const closeLogin = () => {
    setLoginOpen(false);
  };

  const AuthEntry = () =>
    auth.isAuthenticated ? (
      <ProfileMenu />
    ) : (
      <LoginButton type="button" onClick={openLogin}>
        {t("Log in")}
      </LoginButton>
    );

  const getBrandName = () => {
    return i18n.language === "cn" ? "CapCam 3D相机" : "CapCam";
  };

  const HomeMenuItem = () => (
    <CustomNavLinkSmall
      as={Link}
      to="/"
      style={{
        color: "#1d1d1f",
        fontWeight: "500",
        padding: "8px 16px",
        textDecoration: "none",
      }}
    >
      {t("Home")}
    </CustomNavLinkSmall>
  );

  const ExploreMenuItem = () => (
    <CustomNavLinkSmall
      as={Link}
      to="/explore"
      style={{
        color: "#1d1d1f",
        fontWeight: "400",
        padding: "8px 16px",
        textDecoration: "none",
      }}
    >
      {t("Explore")}
    </CustomNavLinkSmall>
  );

  const StoriesMenuItem = () => (
    <CustomNavLinkSmall
      as={Link}
      to="/stories"
      style={{
        color: "#1d1d1f",
        fontWeight: "400",
        padding: "8px 16px",
        textDecoration: "none",
      }}
    >
      {t("Stories")}
    </CustomNavLinkSmall>
  );

  return (
    <HeaderSection>
      <Container>
        <Row justify="space-between" align="middle">
          <div style={{ display: "flex", alignItems: "center", gap: "2rem" }}>
            <LogoContainer to="/" aria-label="homepage">
              <img
                src="/favicon.svg"
                alt="CapCam Logo"
                width="36"
                height="36"
                style={{ marginRight: "10px" }}
              />
              <LogoTitle>{getBrandName()}</LogoTitle>
            </LogoContainer>
            <NotHidden>
              <HomeMenuItem />
              <ExploreMenuItem />
              <StoriesMenuItem />
            </NotHidden>
          </div>
          <NotHidden>
            <AuthEntry />
            <SettingsMenu t={t} i18n={i18n} />
          </NotHidden>
          <MobileCluster>
            <Burger onClick={toggleButton}>
              <Outline />
            </Burger>
            <AuthEntry />
          </MobileCluster>
        </Row>
        <Drawer closable={false} open={visible} onClose={toggleButton}>
          <Col style={{ marginBottom: "2.5rem" }}>
            <Label onClick={toggleButton}>
              <Col span={12}>
                <Menu>Menu</Menu>
              </Col>
              <Col span={12}>
                <Outline />
              </Col>
            </Label>
          </Col>
          <HomeMenuItem />
          <ExploreMenuItem />
          <StoriesMenuItem />
        </Drawer>
        <LoginModal open={loginOpen} onClose={closeLogin} />
      </Container>
    </HeaderSection>
  );
};

export default withTranslation()(Header);
